import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { intlLocale } from '../../lib/locale';
import type { GraphModel } from './graphModel';
import type { SimEdge, SimNode } from './simulation';

/*
 * Edge labels (`SimEdge.label`) come from the backend graph and double as data
 * keys: grouping, lookups and the BOM walks below all match on them. They are
 * never translated in place; only the text shown for them is looked up here and
 * resolved at call time.
 */

const CLASS_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  Kunde: msg`Kunde`, ProduktType: msg`Produkt`, ProduktVersion: msg`Produktversion`,
  KomponenteType: msg`Komponente`, KomponenteVersion: msg`Komponentenversion`,
  ProduktVersionKomponente: msg`Stücklisteneintrag`, ProduktInstanz: msg`Produktinstanz`,
  VulnerabilityEvent: msg`Bewertung`, Benachrichtigung: msg`Benachrichtigung`,
  NotificationInstance: msg`Benachrichtigte Instanz`,
};

/** Display name of a CMDB class; unknown classes (and CVE) show their technical name. */
export function classLabel(klass: string): string {
  const label = CLASS_LABELS[klass];
  return label ? i18n._(label) : klass;
}

/** Display text per known edge label, read from the referencing end. */
const FORWARD_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  'geliefert an': msg`geliefert an`, 'ist ein': msg`ist ein`,
  'Version von': msg`Version von`, 'Vorgänger': msg`Vorgänger`,
  'ausgeliefert mit': msg`ausgeliefert mit`, 'läuft mit': msg`läuft mit`,
  'enthält': msg`enthält`, 'betroffen von': msg`betroffen von`,
  'basiert auf': msg`basiert auf`, 'Kundenversion von': msg`Kundenversion von`,
  'bewertet': msg`bewertet`, 'zu CVE': msg`zu CVE`,
  'aus Bewertung': msg`aus Bewertung`, 'betrifft': msg`betrifft`,
  'wegen': msg`wegen`, 'Stückliste': msg`Stückliste`,
  'an Kunde': msg`an Kunde`, 'Nachtrag zu': msg`Nachtrag zu`, 'genannt in': msg`genannt in`,
};

// Read a reference from either end without inventing a purchase or an impact.
const INVERSE_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  'geliefert an': msg`besitzt`, 'ist ein': msg`hat Instanz`,
  'Version von': msg`hat Version`, 'Vorgänger': msg`hat Nachfolger`,
  'ausgeliefert mit': msg`Auslieferungsversion von`, 'läuft mit': msg`wird eingesetzt in`,
  'enthält': msg`ist enthalten in`, 'betroffen von': msg`betrifft`,
  'basiert auf': msg`Basis von`, 'Kundenversion von': msg`hat Kundenversion`,
  'bewertet': msg`hat Bewertung`, 'zu CVE': msg`hat Bewertung`,
  'aus Bewertung': msg`hat Benachrichtigung`, 'betrifft': msg`hat Benachrichtigung`,
  'wegen': msg`Anlass für Benachrichtigung`, 'Stückliste': msg`hat Stücklisteneintrag`,
  'an Kunde': msg`hat Benachrichtigung`, 'Nachtrag zu': msg`hat Nachtrag`, 'genannt in': msg`nennt Instanz`,
};

/** Display text of `edge` read from `fromId`; unknown labels show as delivered by the API. */
export function relationLabel(edge: SimEdge, fromId: string): string {
  if (edge.source.id === fromId) {
    const forward = FORWARD_LABELS[edge.label];
    return forward ? i18n._(forward) : edge.label;
  }
  const inverse = INVERSE_LABELS[edge.label];
  return inverse ? i18n._(inverse) : `← ${edge.label}`;
}

export interface RelationGroup {
  key: string;
  label: string;
  direction: 'out' | 'in';
  klass: string;
  nodes: SimNode[];
}

export function relationGroups(model: GraphModel, nodeId: string): RelationGroup[] {
  const groups = new Map<string, RelationGroup>();
  for (const edge of model.incident.get(nodeId) ?? []) {
    const outgoing = edge.source.id === nodeId;
    const other = outgoing ? edge.target : edge.source;
    const direction = outgoing ? 'out' : 'in';
    const key = JSON.stringify([direction, edge.label, other.klass]);
    const group = groups.get(key) ?? {
      key, label: relationLabel(edge, nodeId), direction, klass: other.klass, nodes: [],
    };
    if (!group.nodes.some((node) => node.id === other.id)) group.nodes.push(other);
    groups.set(key, group);
  }
  return [...groups.values()].map((group) => ({
    ...group, nodes: group.nodes.sort((a, b) => a.label.localeCompare(b.label, intlLocale(), { numeric: true })),
  }));
}

/*
 * Record sheet ("Datenblatt"): every connection of one record at once, named in
 * domain terms instead of edge labels.
 *
 * Edges point from the card that holds the reference to the card it names, so
 * an OUTGOING edge is a single value of this record (its Kunde, its Produkt …)
 * and becomes a field; INCOMING edges are the records that point here and
 * become lists. The collapsed bill of materials ("enthält") is the one N:M
 * edge and always forms a list.
 */

/**
 * Field per outgoing edge label; anything else is named after the target class.
 * Values are stable field keys — a class name where the field simply names that
 * class, so a reference and the class fallback share one field.
 */
const FIELD_KEYS: Readonly<Record<string, string>> = {
  'geliefert an': 'Kunde', 'ist ein': 'ProduktType', 'ausgeliefert mit': 'deliveredVersion',
  'läuft mit': 'currentVersion', 'Vorgänger': 'predecessorVersion', 'betroffen von': 'CVE',
  'basiert auf': 'baseVersion', 'Kundenversion von': 'ProduktInstanz',
  'bewertet': 'KomponenteVersion', 'zu CVE': 'CVE', 'aus Bewertung': 'VulnerabilityEvent',
  'betrifft': 'ProduktInstanz', 'wegen': 'KomponenteVersion',
};

/** Field names that are not just a class name. */
const FIELD_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  deliveredVersion: msg`Ausgelieferte Version`,
  currentVersion: msg`Aktuelle Version`,
  predecessorVersion: msg`Vorgängerversion`,
  baseVersion: msg`Basisversion`,
};

/** Field order by field key; unranked fields follow alphabetically. */
const FIELD_ORDER = [
  'Kunde', 'ProduktType', 'KomponenteType', 'ProduktInstanz', 'deliveredVersion', 'currentVersion',
  'predecessorVersion', 'baseVersion', 'KomponenteVersion', 'CVE', 'VulnerabilityEvent',
];

/**
 * List per incoming edge label; anything else is the source class's plural.
 * Keys are the source class where the title is just its plural, so an edge and
 * the class fallback share one list.
 */
const LIST_KEYS: Readonly<Record<string, string>> = {
  'geliefert an': 'ProduktInstanz', 'ist ein': 'ProduktInstanz', 'Version von': 'versions',
  'Vorgänger': 'successorVersions', 'ausgeliefert mit': 'deliveredTo', 'läuft mit': 'inUseAt',
  'betroffen von': 'affectedComponentVersions', 'basiert auf': 'customVersions',
  'Kundenversion von': 'customVersions', 'bewertet': 'VulnerabilityEvent', 'zu CVE': 'VulnerabilityEvent',
  'aus Bewertung': 'Benachrichtigung', 'betrifft': 'Benachrichtigung', 'wegen': 'Benachrichtigung',
  'enthält': 'containedIn',
};

/** Outgoing edges that are N:M and therefore a list, with their list key. */
const OUTGOING_LIST_KEYS: Readonly<Record<string, string>> = { 'enthält': 'bom' };

/** List titles by list key. */
const LIST_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  ProduktInstanz: msg`Produktinstanzen`, versions: msg`Versionen`,
  successorVersions: msg`Nachfolgeversionen`, deliveredTo: msg`Ausgeliefert an`, inUseAt: msg`Im Einsatz bei`,
  affectedComponentVersions: msg`Betroffene Komponentenversionen`, customVersions: msg`Kundenversionen`,
  VulnerabilityEvent: msg`Bewertungen`, Benachrichtigung: msg`Benachrichtigungen`,
  containedIn: msg`Enthalten in`, bom: msg`Stückliste`,
};

export interface SheetField { key: string; label: string; node: SimNode }
export interface SheetList { key: string; label: string; nodes: SimNode[] }
export interface RecordSheet { fields: SheetField[]; lists: SheetList[] }

const byLabel = (a: SimNode, b: SimNode): number => a.label.localeCompare(b.label, intlLocale(), { numeric: true });

interface Bucket { label: string; nodes: SimNode[] }

interface SheetSlot {
  map: 'fields' | 'lists';
  key: string;
  /** Resolved only when the slot is first filled, in the active language. */
  label: (plurals: Readonly<Record<string, string>>) => string;
}

/** Where an edge lands on the record sheet: field or list, its stable key and its display name. */
function sheetSlot(edgeLabel: string, outgoing: boolean, klass: string): SheetSlot {
  const listLabel = (key: string) => (plurals: Readonly<Record<string, string>>): string => {
    const label = LIST_LABELS[key];
    return label ? i18n._(label) : plurals[klass] ?? classLabel(klass);
  };
  const outgoingList = outgoing ? OUTGOING_LIST_KEYS[edgeLabel] : undefined;
  if (outgoingList) return { map: 'lists', key: outgoingList, label: listLabel(outgoingList) };
  if (outgoing) {
    const key = FIELD_KEYS[edgeLabel] ?? klass;
    const label = FIELD_LABELS[key];
    return { map: 'fields', key, label: () => (label ? i18n._(label) : classLabel(key)) };
  }
  const key = LIST_KEYS[edgeLabel] ?? klass;
  return { map: 'lists', key, label: listLabel(key) };
}

/**
 * The record sheet of `nodeId`. `excludeId` (the step the user came from) is
 * left out: it is already on screen in the path. `plurals` names the lists the
 * label table does not cover, keyed by class.
 *
 * Fields and lists are grouped and ranked by stable keys; the label is only
 * resolved for display, in the active UI language.
 */
export function recordSheet(
  model: GraphModel,
  nodeId: string,
  plurals: Readonly<Record<string, string>> = {},
  excludeId: string | null = null,
): RecordSheet {
  const fields = new Map<string, Bucket>();
  const lists = new Map<string, Bucket>();
  const add = (map: Map<string, Bucket>, key: string, label: () => string, node: SimNode): void => {
    const bucket = map.get(key) ?? { label: label(), nodes: [] };
    if (!bucket.nodes.some((entry) => entry.id === node.id)) bucket.nodes.push(node);
    map.set(key, bucket);
  };
  for (const edge of model.incident.get(nodeId) ?? []) {
    const outgoing = edge.source.id === nodeId;
    const other = outgoing ? edge.target : edge.source;
    if (other.id === excludeId || other.id === nodeId) continue;
    const { map, key, label } = sheetSlot(edge.label, outgoing, other.klass);
    add(map === 'fields' ? fields : lists, key, () => label(plurals), other);
  }
  // A reference that unexpectedly names several records reads as a list.
  for (const [key, bucket] of fields) {
    if (bucket.nodes.length > 1) {
      fields.delete(key);
      for (const node of bucket.nodes) add(lists, `field:${key}`, () => bucket.label, node);
    }
  }
  const rank = (key: string): number => {
    const index = FIELD_ORDER.indexOf(key);
    return index < 0 ? FIELD_ORDER.length : index;
  };
  const locale = intlLocale();
  return {
    fields: [...fields].map(([key, { label, nodes }]) => ({ key, label, node: nodes[0]! }))
      .sort((a, b) => rank(a.key) - rank(b.key) || a.label.localeCompare(b.label, locale)),
    lists: [...lists].map(([key, { label, nodes }]) => ({ key, label, nodes: nodes.toSorted(byLabel) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale)),
  };
}

/*
 * Vulnerabilities derived from the bill of materials. The graph's own
 * CVE → Produkt link (CveProduktType) is only ever added by the KEV scan and
 * never pruned, and it does not say which version; walking the Stückliste is
 * always current and names the version and component a CVE comes in through:
 *
 *   ProduktType ←"Version von"— ProduktVersion —"enthält"→ KomponenteVersion —"betroffen von"→ CVE
 */

export interface DerivedVulnerability { cve: SimNode; components: SimNode[]; versions: SimNode[] }
export interface DerivedProduct { product: SimNode; versions: SimNode[] }

function linked(model: GraphModel, id: string, label: string, direction: 'out' | 'in', klass: string): SimNode[] {
  return (model.incident.get(id) ?? []).flatMap((edge) => {
    if (edge.label !== label) return [];
    const from = direction === 'out' ? edge.source : edge.target;
    const to = direction === 'out' ? edge.target : edge.source;
    return from.id === id && to.klass === klass ? [to] : [];
  });
}

const STATE_RANK: Readonly<Record<string, number>> = { danger: 0, warn: 1, ok: 2 };
const byRisk = (a: SimNode, b: SimNode): number =>
  (STATE_RANK[a.state] ?? 3) - (STATE_RANK[b.state] ?? 3) || byLabel(a, b);

function addUnique(list: SimNode[], node: SimNode): void {
  if (!list.some((entry) => entry.id === node.id)) list.push(node);
}

/** CVEs reaching a Produkt or one ProduktVersion through its components; [] for any other class. */
export function bomVulnerabilities(model: GraphModel, nodeId: string): DerivedVulnerability[] {
  const node = model.byId.get(nodeId);
  if (!node) return [];
  const versions = node.klass === 'ProduktType' ? linked(model, nodeId, 'Version von', 'in', 'ProduktVersion')
    : node.klass === 'ProduktVersion' ? [node] : [];
  const byCve = new Map<string, DerivedVulnerability>();
  for (const version of versions) {
    for (const component of linked(model, version.id, 'enthält', 'out', 'KomponenteVersion')) {
      for (const cve of linked(model, component.id, 'betroffen von', 'out', 'CVE')) {
        const entry = byCve.get(cve.id) ?? { cve, components: [], versions: [] };
        addUnique(entry.components, component);
        addUnique(entry.versions, version);
        byCve.set(cve.id, entry);
      }
    }
  }
  return [...byCve.values()]
    .map((entry) => ({ ...entry, components: entry.components.sort(byLabel), versions: entry.versions.sort(byLabel) }))
    .sort((a, b) => byRisk(a.cve, b.cve));
}

/** The reverse walk: Produkte (with the affected versions) a CVE reaches; [] for any other class. */
export function bomAffectedProducts(model: GraphModel, nodeId: string): DerivedProduct[] {
  const node = model.byId.get(nodeId);
  if (!node || node.klass !== 'CVE') return [];
  const byProduct = new Map<string, DerivedProduct>();
  for (const component of linked(model, nodeId, 'betroffen von', 'in', 'KomponenteVersion')) {
    for (const version of linked(model, component.id, 'enthält', 'in', 'ProduktVersion')) {
      for (const product of linked(model, version.id, 'Version von', 'out', 'ProduktType')) {
        const entry = byProduct.get(product.id) ?? { product, versions: [] };
        addUnique(entry.versions, version);
        byProduct.set(product.id, entry);
      }
    }
  }
  return [...byProduct.values()]
    .map((entry) => ({ ...entry, versions: entry.versions.sort(byLabel) }))
    .sort((a, b) => byLabel(a.product, b.product));
}

/*
 * Customer summary: the questions one asks about a Kunde, answered two hops
 * away without clicking through each Produktinstanz.
 *
 * An instance runs the Stückliste of its current version ("läuft mit"). A
 * deviating instance runs a custom version of its own ("Kundenversion von",
 * DEV-107), so the Stückliste is always the whole truth.
 */

export interface CustomerSummary {
  instances: number;
  /** Instances whose effective configuration holds a KEV-affected component version. */
  kevAffected: number;
  notificationsOpen: number;
  notificationsSent: number;
  /** Instances running a custom version of their own. */
  customVersions: number;
}

/** The component versions an instance actually runs. */
export function effectiveComponents(model: GraphModel, instanceId: string): SimNode[] {
  const version = linked(model, instanceId, 'läuft mit', 'out', 'ProduktVersion')[0];
  return version ? linked(model, version.id, 'enthält', 'out', 'KomponenteVersion') : [];
}

/** Null for anything but a Kunde. */
export function customerSummary(model: GraphModel, nodeId: string): CustomerSummary | null {
  if (model.byId.get(nodeId)?.klass !== 'Kunde') return null;
  const instances = linked(model, nodeId, 'geliefert an', 'in', 'ProduktInstanz');
  const summary: CustomerSummary = { instances: instances.length, kevAffected: 0, notificationsOpen: 0, notificationsSent: 0, customVersions: 0 };
  for (const instance of instances) {
    if (effectiveComponents(model, instance.id).some((component) => component.state === 'danger')) summary.kevAffected += 1;
    for (const notice of linked(model, instance.id, 'betrifft', 'in', 'Benachrichtigung')) {
      if (notice.state === 'ok') summary.notificationsSent += 1; else summary.notificationsOpen += 1;
    }
    const version = linked(model, instance.id, 'läuft mit', 'out', 'ProduktVersion')[0];
    if (version && linked(model, version.id, 'Kundenversion von', 'out', 'ProduktInstanz').length > 0) {
      summary.customVersions += 1;
    }
  }
  return summary;
}

export function matchesNode(node: SimNode, term: string): boolean {
  const locale = intlLocale();
  const query = term.trim().toLocaleLowerCase(locale);
  return `${node.label} ${node.detail} ${node.context ?? ''} ${classLabel(node.klass)} ${node.cardId}`.toLocaleLowerCase(locale).includes(query);
}

export function initialRoot(model: GraphModel): string | null {
  return model.nodes.find((node) => node.klass === 'Kunde' && model.neighbours.has(node.id))?.id
    ?? model.nodes.find((node) => model.neighbours.has(node.id))?.id
    ?? model.nodes[0]?.id ?? null;
}
